import React, { useState, useEffect } from 'react';
import {
  X,
  Mail,
  Building,
  Briefcase,
  MessageSquare,
  Shield,
  Calendar,
  CheckCircle,
  Clock,
  Sparkles,
  Video,
  PhoneCall,
  Camera,
  Search,
  Filter,
  CheckSquare,
  Plus,
  ExternalLink,
  ChevronDown,
  Info,
  Layers,
  Paperclip,
  User as UserIcon,
  Moon,
  Sun,
  Loader2,
  AlertCircle,
  RefreshCw,
  FolderKanban,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useCall } from '../../context/CallContext.tsx';
import { useRealtime } from '../../context/RealtimeContext.tsx';
import { useUserProfileModal } from '../../context/UserProfileModalContext.tsx';
import { RoleBadge } from '../common/Badge.tsx';
import { EditProfileModal } from '../profile/EditProfileModal.tsx';
import { User, Task } from '../../../shared/types.ts';
import { useEscapeKey } from '../../hooks/useEscapeKey.ts';

interface ProfileData {
  member: User & { departmentName?: string; isOnline?: boolean };
  stats: {
    totalAssigned: number;
    completedTasks: number;
    inProgressTasks: number;
    todoTasks: number;
    reviewTasks: number;
    urgentCount: number;
    totalComments: number;
    totalActivities: number;
  };
  priorities: any[];
  tasks: any[];
  comments: any[];
  activities: Array<{
    id: string;
    action: string;
    title: string;
    description: string;
    entityType: string;
    entityId: string;
    metadata: any;
    createdAt: string;
  }>;
}

interface UserProfileDrawerProps {
  user: User | null;
  onClose: () => void;
  isDocked?: boolean;
  onNavigateToChat?: (channelId?: string) => void;
  onScheduleMeeting?: (user: User) => void;
}

export const UserProfileDrawer: React.FC<UserProfileDrawerProps> = ({
  user,
  onClose,
  isDocked = false,
  onNavigateToChat,
  onScheduleMeeting,
}) => {
  useEscapeKey(!isDocked, onClose);
  const { token, user: currentUser } = useAuth();
  const { startDirectCall } = useCall();
  const { onlineUserIds } = useRealtime();
  const { navigateToChatWithUser } = useUserProfileModal();

  const [isEditSelfOpen, setIsEditSelfOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'activity' | 'tasks' | 'comments' | 'calendar' | 'details'>('activity');
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Activity search & filter
  const [activitySearch, setActivitySearch] = useState('');
  const [activityFilter, setActivityFilter] = useState<'all' | 'task' | 'comment' | 'file' | 'project'>('all');
  const [isSearchInputVisible, setIsSearchInputVisible] = useState(false);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

  useEffect(() => {
    if (!user || !token) {
      setProfileData(null);
      return;
    }

    setIsLoadingProfile(true);
    setProfileError(null);

    fetch(`/api/team/members/${user.id}/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error('Failed to load profile data');
        }
        return res.json();
      })
      .then((data) => {
        setProfileData(data);
      })
      .catch((err) => {
        console.error('Error fetching profile details:', err);
        setProfileError(err.message || 'Unable to load profile');
      })
      .finally(() => {
        setIsLoadingProfile(false);
      });
  }, [user?.id, token]);

  if (!user) return null;

  const isSelf = currentUser?.id === user.id;
  const isUserOnline = isSelf
    ? true
    : user
    ? typeof (onlineUserIds as any)?.has === 'function'
      ? (onlineUserIds as Set<string>).has(user.id)
      : Array.isArray(onlineUserIds)
      ? (onlineUserIds as string[]).includes(user.id)
      : false
    : false;

  const handleStartChat = () => {
    navigateToChatWithUser(user);
    if (!isDocked) onClose();
    if (onNavigateToChat) {
      onNavigateToChat();
    }
  };

  const handleStartCall = (withVideo = false) => {
    startDirectCall(
      {
        id: user.id,
        name: user.fullName,
        avatarUrl: user.avatarUrl,
        role: user.title || user.role,
      },
      withVideo
    );
  };

  const handleOpenTask = (taskId: string) => {
    if (!taskId) return;
    window.dispatchEvent(new CustomEvent('open-task-modal', { detail: { taskId } }));
  };

  // Local time calculation
  const now = new Date();
  const localTimeStr = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  const isNight = now.getHours() < 6 || now.getHours() >= 20;

  // Filter activities
  const filteredActivities = (profileData?.activities || []).filter((act) => {
    if (activityFilter === 'task' && !act.action.includes('TASK') && act.entityType !== 'task') return false;
    if (activityFilter === 'comment' && !act.action.includes('COMMENT') && act.entityType !== 'comment') return false;
    if (activityFilter === 'file' && !act.action.includes('FILE') && act.entityType !== 'file') return false;
    if (activityFilter === 'project' && !act.action.includes('PROJECT') && act.entityType !== 'project') return false;

    if (activitySearch.trim()) {
      const q = activitySearch.toLowerCase();
      const matchTitle = act.title.toLowerCase().includes(q);
      const matchDesc = act.description.toLowerCase().includes(q);
      const matchAction = act.action.toLowerCase().includes(q);
      if (!matchTitle && !matchDesc && !matchAction) return false;
    }

    return true;
  });

  const formatActivityTime = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const diffMs = Date.now() - d.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays}d ago`;

      return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
    } catch {
      return dateStr;
    }
  };

  const getActivityIcon = (act: any) => {
    if (act.action.includes('TASK_STATUS') || act.action === 'TASK_COMPLETE') {
      return <CheckCircle className="w-4 h-4 text-emerald-500" />;
    }
    if (act.action.includes('TASK')) {
      return <CheckSquare className="w-4 h-4 text-brand-600" />;
    }
    if (act.action.includes('COMMENT')) {
      return <MessageSquare className="w-4 h-4 text-purple-500" />;
    }
    if (act.action.includes('FILE')) {
      return <Paperclip className="w-4 h-4 text-amber-500" />;
    }
    if (act.action.includes('PROJECT')) {
      return <Layers className="w-4 h-4 text-blue-500" />;
    }
    if (act.action.includes('LOGIN')) {
      return <Shield className="w-4 h-4 text-teal-500" />;
    }
    return <Sparkles className="w-4 h-4 text-surface-400" />;
  };

  const drawerContent = (
    <div className="w-full h-full bg-white dark:bg-surface-900 flex flex-col font-sans text-surface-900 dark:text-surface-100 select-text overflow-hidden">
      {/* Drawer Header & Actions */}
      <div className="p-4 sm:p-5 pb-3 border-b border-surface-100 dark:border-surface-800 shrink-0">
        <div className="flex items-start justify-between">
          {/* User Identity Block */}
          <div className="flex items-start space-x-3.5 min-w-0">
            {/* Squircle Avatar */}
            <div className="relative shrink-0">
              {user.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={user.fullName}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover shadow-sm ring-2 ring-surface-200 dark:ring-surface-700"
                />
              ) : (
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-amber-400 text-white font-extrabold text-2xl sm:text-3xl flex items-center justify-center shadow-sm select-none">
                  {user.fullName.slice(0, 2).toUpperCase()}
                </div>
              )}
              <span
                className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full ring-2 ring-white dark:ring-surface-900 ${
                  isUserOnline ? 'bg-emerald-500' : 'bg-surface-400'
                }`}
                title={isUserOnline ? 'Online' : 'Offline'}
              />
            </div>

            {/* Names & Actions */}
            <div className="min-w-0 pt-0.5">
              <div className="flex items-center space-x-1.5">
                <h2 className="text-lg sm:text-xl font-bold tracking-tight text-surface-900 dark:text-surface-100 truncate">
                  {user.fullName}
                </h2>
                <ChevronDown className="w-4 h-4 text-surface-400 shrink-0" />
              </div>
              <p className="text-xs text-surface-500 dark:text-surface-400 truncate mt-0.5 font-medium">
                {user.title || user.departmentName || 'Team Contributor'}
              </p>

              {/* Status Dot */}
              <div className="flex items-center space-x-1.5 mt-1.5">
                <span
                  className={`w-2 h-2 rounded-full ${
                    isUserOnline ? 'bg-emerald-500' : 'border border-surface-400 bg-transparent'
                  }`}
                />
                <span className={`text-xs font-semibold ${isUserOnline ? 'text-emerald-600 dark:text-emerald-400' : 'text-surface-400'}`}>
                  {isUserOnline ? 'Online' : 'Offline'}
                </span>
              </div>

              {/* Quick Action Buttons */}
              <div className="flex items-center space-x-2 mt-3">
                {!isSelf && (
                  <>
                    <button
                      type="button"
                      onClick={handleStartChat}
                      className="p-1.5 sm:px-2.5 sm:py-1.5 rounded-lg border border-surface-200 dark:border-surface-700 hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-700 dark:text-surface-200 text-xs font-medium flex items-center space-x-1.5 transition-colors"
                      title="Direct Message"
                    >
                      <MessageSquare className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
                      <span className="hidden sm:inline">Message</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleStartCall(false)}
                      className="p-1.5 sm:px-2.5 sm:py-1.5 rounded-lg border border-surface-200 dark:border-surface-700 hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-700 dark:text-surface-200 text-xs font-medium flex items-center space-x-1.5 transition-colors"
                      title="Audio Call"
                    >
                      <PhoneCall className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
                      <span className="hidden sm:inline">Audio</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleStartCall(true)}
                      className="p-1.5 sm:px-2.5 sm:py-1.5 rounded-lg border border-surface-200 dark:border-surface-700 hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-700 dark:text-surface-200 text-xs font-medium flex items-center space-x-1.5 transition-colors"
                      title="Video Call"
                    >
                      <Video className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
                      <span className="hidden sm:inline">Video</span>
                    </button>
                  </>
                )}

                {isSelf && (
                  <button
                    type="button"
                    onClick={() => setIsEditSelfOpen(true)}
                    className="px-2.5 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold flex items-center space-x-1.5 transition-colors shadow-xs"
                  >
                    <Camera className="w-3.5 h-3.5" />
                    <span>Edit Profile</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => {
                    if (user && token) {
                      setIsLoadingProfile(true);
                      fetch(`/api/team/members/${user.id}/profile`, {
                        headers: { Authorization: `Bearer ${token}` },
                      })
                        .then((r) => r.json())
                        .then((d) => setProfileData(d))
                        .finally(() => setIsLoadingProfile(false));
                    }
                  }}
                  className="px-2.5 py-1.5 rounded-lg border border-surface-200 dark:border-surface-700 hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-600 dark:text-surface-300 text-xs font-medium flex items-center space-x-1.5 transition-colors"
                  title="Refresh Activity"
                >
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                  <span>View Update</span>
                </button>
              </div>
            </div>
          </div>

          {/* Close 'X' Button */}
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 transition-colors"
            aria-label="Close profile drawer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Tab Bar */}
        <div className="flex items-center space-x-5 sm:space-x-6 mt-4 -mb-3 border-b border-surface-200 dark:border-surface-800 overflow-x-auto no-scrollbar">
          <button
            type="button"
            onClick={() => setActiveTab('activity')}
            className={`pb-2.5 text-xs font-bold transition-colors whitespace-nowrap border-b-2 ${
              activeTab === 'activity'
                ? 'border-brand-600 dark:border-brand-400 text-brand-600 dark:text-brand-400'
                : 'border-transparent text-surface-500 hover:text-surface-800 dark:hover:text-surface-200'
            }`}
          >
            Activity
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('tasks')}
            className={`pb-2.5 text-xs font-bold transition-colors whitespace-nowrap border-b-2 ${
              activeTab === 'tasks'
                ? 'border-brand-600 dark:border-brand-400 text-brand-600 dark:text-brand-400'
                : 'border-transparent text-surface-500 hover:text-surface-800 dark:hover:text-surface-200'
            }`}
          >
            Tasks ({profileData?.stats?.totalAssigned ?? 0})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('comments')}
            className={`pb-2.5 text-xs font-bold transition-colors whitespace-nowrap border-b-2 ${
              activeTab === 'comments'
                ? 'border-brand-600 dark:border-brand-400 text-brand-600 dark:text-brand-400'
                : 'border-transparent text-surface-500 hover:text-surface-800 dark:hover:text-surface-200'
            }`}
          >
            Comments ({profileData?.stats?.totalComments ?? 0})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('calendar')}
            className={`pb-2.5 text-xs font-bold transition-colors whitespace-nowrap border-b-2 ${
              activeTab === 'calendar'
                ? 'border-brand-600 dark:border-brand-400 text-brand-600 dark:text-brand-400'
                : 'border-transparent text-surface-500 hover:text-surface-800 dark:hover:text-surface-200'
            }`}
          >
            Calendar
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('details')}
            className={`pb-2.5 text-xs font-bold transition-colors whitespace-nowrap border-b-2 ${
              activeTab === 'details'
                ? 'border-brand-600 dark:border-brand-400 text-brand-600 dark:text-brand-400'
                : 'border-transparent text-surface-500 hover:text-surface-800 dark:hover:text-surface-200'
            }`}
          >
            Details
          </button>
        </div>
      </div>

      {/* Drawer Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-5">
        {/* Metadata quick-info rows */}
        <div className="space-y-2 text-xs text-surface-600 dark:text-surface-400">
          <div className="flex items-center space-x-2.5">
            <Mail className="w-4 h-4 text-surface-400 shrink-0" />
            <a
              href={`mailto:${user.email}`}
              className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors truncate"
            >
              {user.email}
            </a>
          </div>

          <div className="flex items-center space-x-2.5">
            <Clock className="w-4 h-4 text-surface-400 shrink-0" />
            <span>
              {localTimeStr} local time {isNight ? '(Night)' : '(Work hours)'}
            </span>
          </div>

          <div className="flex items-center space-x-2.5">
            <UserIcon className="w-4 h-4 text-surface-400 shrink-0" />
            <span>
              {user.departmentName ? `${user.departmentName} Department` : 'No manager assigned'}
            </span>
          </div>
        </div>

        <hr className="border-surface-100 dark:border-surface-800" />

        {/* TAB 1: ACTIVITY (Lifetime Activity View) */}
        {activeTab === 'activity' && (
          <div className="space-y-5">
            {/* Priorities Sub-section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-1.5 text-xs font-bold text-surface-900 dark:text-surface-100">
                  <span>Priorities</span>
                  <Info className="w-3.5 h-3.5 text-surface-400" />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('open-task-modal', { detail: { assigneeId: user.id } }));
                  }}
                  className="text-xs font-bold text-brand-600 hover:text-brand-700 flex items-center space-x-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add</span>
                </button>
              </div>

              {profileData?.priorities && profileData.priorities.length > 0 ? (
                <div className="space-y-2">
                  {profileData.priorities.slice(0, 3).map((pri) => (
                    <div
                      key={pri.id}
                      onClick={() => handleOpenTask(pri.id)}
                      className="p-3 rounded-xl border border-rose-200 dark:border-rose-900/50 bg-rose-50/40 dark:bg-rose-950/20 hover:bg-rose-50 dark:hover:bg-rose-950/40 cursor-pointer transition-colors flex items-center justify-between"
                    >
                      <div className="min-w-0 pr-2">
                        <span className="text-xs font-bold text-surface-900 dark:text-surface-100 block truncate">
                          {pri.title}
                        </span>
                        <span className="text-[10px] text-surface-500 block truncate mt-0.5">
                          {pri.projectName || 'Project'} • {pri.status.replace('_', ' ')}
                        </span>
                      </div>
                      <span className="px-2 py-0.5 rounded-md bg-rose-100 dark:bg-rose-900/60 text-rose-700 dark:text-rose-300 text-[10px] font-black uppercase shrink-0">
                        {pri.priority}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('open-task-modal', { detail: { assigneeId: user.id } }));
                  }}
                  className="border border-dashed border-surface-200 dark:border-surface-700 rounded-xl p-4 text-center text-xs text-surface-400 hover:text-surface-600 dark:hover:text-surface-300 hover:border-surface-300 cursor-pointer transition-colors flex items-center justify-center space-x-1.5"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add important tasks here.</span>
                </div>
              )}
            </div>

            {/* Lifetime Activity Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-surface-900 dark:text-surface-100">
                  Activity
                </h3>

                <div className="flex items-center space-x-1">
                  <button
                    type="button"
                    onClick={() => setIsSearchInputVisible(!isSearchInputVisible)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      isSearchInputVisible || activitySearch
                        ? 'bg-brand-50 text-brand-600 dark:bg-brand-950/40'
                        : 'hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-500'
                    }`}
                    title="Search activity"
                  >
                    <Search className="w-4 h-4" />
                  </button>

                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        activityFilter !== 'all'
                          ? 'bg-brand-50 text-brand-600 dark:bg-brand-950/40'
                          : 'hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-500'
                      }`}
                      title="Filter activity type"
                    >
                      <Filter className="w-4 h-4" />
                    </button>

                    {isFilterDropdownOpen && (
                      <div className="absolute right-0 top-8 z-30 w-36 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl shadow-elevated py-1 text-xs text-surface-700 dark:text-surface-200 animate-in fade-in zoom-in-95">
                        {(['all', 'task', 'comment', 'file', 'project'] as const).map((ft) => (
                          <button
                            key={ft}
                            type="button"
                            onClick={() => {
                              setActivityFilter(ft);
                              setIsFilterDropdownOpen(false);
                            }}
                            className={`w-full text-left px-3 py-1.5 hover:bg-surface-100 dark:hover:bg-surface-700 capitalize flex items-center justify-between ${
                              activityFilter === ft ? 'font-bold text-brand-600 dark:text-brand-400' : ''
                            }`}
                          >
                            <span>{ft === 'all' ? 'All Activities' : `${ft}s`}</span>
                            {activityFilter === ft && <CheckCircle className="w-3 h-3" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Search Bar Input */}
              {isSearchInputVisible && (
                <div className="relative mb-3 animate-in slide-in-from-top-1">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-surface-400" />
                  <input
                    type="text"
                    value={activitySearch}
                    onChange={(e) => setActivitySearch(e.target.value)}
                    placeholder="Filter lifetime activity..."
                    className="w-full pl-8 pr-8 py-1.5 bg-surface-50 dark:bg-surface-800/80 border border-surface-200 dark:border-surface-700 rounded-xl text-xs text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                  {activitySearch && (
                    <button
                      type="button"
                      onClick={() => setActivitySearch('')}
                      className="absolute right-2.5 top-2 text-surface-400 hover:text-surface-600"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}

              {/* Activity Feed Timeline */}
              {isLoadingProfile ? (
                <div className="flex flex-col items-center justify-center py-12 text-surface-400">
                  <Loader2 className="w-6 h-6 animate-spin mb-2 text-brand-500" />
                  <span className="text-xs font-semibold">Loading lifetime activity...</span>
                </div>
              ) : filteredActivities.length > 0 ? (
                <div className="space-y-3 relative before:absolute before:left-3.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-surface-100 dark:before:bg-surface-800">
                  {filteredActivities.map((act) => {
                    const isTaskLinkable = act.entityType === 'task' && act.entityId;
                    return (
                      <div
                        key={act.id}
                        onClick={() => {
                          if (isTaskLinkable) handleOpenTask(act.entityId);
                        }}
                        className={`relative flex items-start space-x-3 p-2.5 rounded-xl transition-all ${
                          isTaskLinkable
                            ? 'hover:bg-surface-50 dark:hover:bg-surface-800/60 cursor-pointer group'
                            : ''
                        }`}
                      >
                        <div className="relative z-10 w-7 h-7 rounded-full bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 flex items-center justify-center shrink-0 shadow-xs">
                          {getActivityIcon(act)}
                        </div>

                        <div className="flex-1 min-w-0 pt-0.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-surface-900 dark:text-surface-100 truncate">
                              {act.title}
                            </span>
                            <span className="text-[10px] text-surface-400 shrink-0 ml-2">
                              {formatActivityTime(act.createdAt)}
                            </span>
                          </div>

                          {act.description && (
                            <p className="text-xs text-surface-600 dark:text-surface-400 mt-0.5 line-clamp-2">
                              {act.description}
                            </p>
                          )}

                          {isTaskLinkable && (
                            <div className="flex items-center space-x-1 text-[10px] text-brand-600 dark:text-brand-400 font-semibold mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <span>View task details</span>
                              <ExternalLink className="w-2.5 h-2.5" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* ClickUp-style Empty State Card Illustration */
                <div className="py-12 flex flex-col items-center justify-center text-center">
                  <div className="relative mb-4 w-28 h-20 flex items-center justify-center">
                    <div className="absolute w-24 h-8 bg-surface-100 dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700 transform -rotate-6 shadow-xs" />
                    <div className="absolute w-24 h-8 bg-white dark:bg-surface-750 rounded-lg border border-surface-200 dark:border-surface-700 transform rotate-3 shadow-sm flex items-center px-2 space-x-1.5">
                      <div className="w-2 h-2 rounded-full bg-surface-300 dark:bg-surface-600" />
                      <div className="w-12 h-1.5 rounded-full bg-surface-200 dark:bg-surface-700" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 flex items-center justify-center text-surface-400 shadow-xs">
                      <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                    </div>
                  </div>
                  <p className="text-xs font-bold text-surface-700 dark:text-surface-300">No activity yet</p>
                  <p className="text-[11px] text-surface-400 mt-1 max-w-[200px]">
                    {activitySearch || activityFilter !== 'all'
                      ? 'Try clearing the search query or filter.'
                      : 'Lifetime actions will automatically appear here as they happen.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: TASKS */}
        {activeTab === 'tasks' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs font-bold text-surface-700 dark:text-surface-300">
              <span>Assigned Tasks ({profileData?.tasks?.length || 0})</span>
              <span className="text-emerald-600 dark:text-emerald-400 text-[11px]">
                {profileData?.stats?.completedTasks || 0} completed
              </span>
            </div>

            {isLoadingProfile ? (
              <div className="flex justify-center py-10">
                <Loader2 className="w-5 h-5 animate-spin text-brand-500" />
              </div>
            ) : (profileData?.tasks || []).length > 0 ? (
              <div className="space-y-2">
                {profileData!.tasks.map((task: any) => (
                  <div
                    key={task.id}
                    onClick={() => handleOpenTask(task.id)}
                    className="p-3 rounded-xl border border-surface-200 dark:border-surface-700 hover:border-brand-500 bg-white dark:bg-surface-800/60 hover:bg-surface-50 dark:hover:bg-surface-800 transition-all cursor-pointer shadow-xs group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 pr-2">
                        <span className="text-xs font-bold text-surface-900 dark:text-surface-100 block truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                          {task.title}
                        </span>
                        <span className="text-[10px] text-surface-500 block truncate mt-0.5">
                          {task.projectName || 'Project'} • Due: {task.dueDate || 'No date'}
                        </span>
                      </div>

                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase shrink-0 ${
                          task.status === 'done'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                            : task.status === 'in_progress'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300'
                            : 'bg-surface-100 text-surface-700 dark:bg-surface-700 dark:text-surface-300'
                        }`}
                      >
                        {task.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-10 text-center text-xs text-surface-400">
                No tasks assigned to this user.
              </div>
            )}
          </div>
        )}

        {/* TAB 3: COMMENTS */}
        {activeTab === 'comments' && (
          <div className="space-y-3">
            <div className="text-xs font-bold text-surface-700 dark:text-surface-300">
              Lifetime Comments ({profileData?.comments?.length || 0})
            </div>

            {isLoadingProfile ? (
              <div className="flex justify-center py-10">
                <Loader2 className="w-5 h-5 animate-spin text-brand-500" />
              </div>
            ) : (profileData?.comments || []).length > 0 ? (
              <div className="space-y-2.5">
                {profileData!.comments.map((c: any) => (
                  <div
                    key={c.id}
                    onClick={() => {
                      if (c.entityType === 'task') handleOpenTask(c.entityId);
                    }}
                    className="p-3 rounded-xl border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800/60 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between text-[10px] text-surface-400 mb-1">
                      <span className="font-semibold text-brand-600 dark:text-brand-400 truncate">
                        {c.entityTitle || `${c.entityType} #${c.entityId}`}
                      </span>
                      <span>{formatActivityTime(c.createdAt)}</span>
                    </div>
                    <p className="text-xs text-surface-700 dark:text-surface-200 line-clamp-2">
                      "{c.content}"
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-10 text-center text-xs text-surface-400">
                No comments made by this user yet.
              </div>
            )}
          </div>
        )}

        {/* TAB 4: CALENDAR */}
        {activeTab === 'calendar' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-surface-900 dark:text-surface-100">
                1-on-1 Schedule & Availability
              </span>
              <button
                type="button"
                onClick={() => {
                  if (onScheduleMeeting) {
                    onScheduleMeeting(user);
                  } else {
                    window.dispatchEvent(new CustomEvent('open-schedule-meeting', { detail: { user } }));
                  }
                }}
                className="px-2.5 py-1 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold flex items-center space-x-1"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Book 1-on-1</span>
              </button>
            </div>

            <div className="p-4 rounded-2xl bg-surface-50 dark:bg-surface-800/50 border border-surface-200 dark:border-surface-700 text-center">
              <Calendar className="w-8 h-8 text-brand-500 mx-auto mb-2" />
              <h5 className="text-xs font-bold text-surface-900 dark:text-surface-100">
                Meet with {user.fullName}
              </h5>
              <p className="text-[11px] text-surface-500 mt-1">
                Local time is currently {localTimeStr}. Select a time slot to sync up on project deliverables.
              </p>
              <button
                type="button"
                onClick={() => {
                  if (onScheduleMeeting) {
                    onScheduleMeeting(user);
                  } else {
                    window.dispatchEvent(new CustomEvent('open-schedule-meeting', { detail: { user } }));
                  }
                }}
                className="mt-3 px-3 py-1.5 rounded-xl border border-brand-500 text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-950/40 text-xs font-semibold inline-flex items-center space-x-1.5"
              >
                <Clock className="w-3.5 h-3.5" />
                <span>Check Available Times</span>
              </button>
            </div>
          </div>
        )}

        {/* TAB 5: DETAILS */}
        {activeTab === 'details' && (
          <div className="space-y-4 text-xs">
            <div className="p-4 rounded-2xl bg-surface-50 dark:bg-surface-800/50 border border-surface-200 dark:border-surface-700 space-y-3">
              <div className="flex justify-between items-center py-1 border-b border-surface-200/50 dark:border-surface-700/50">
                <span className="text-surface-400 font-medium">Role</span>
                <RoleBadge role={user.role} />
              </div>

              <div className="flex justify-between items-center py-1 border-b border-surface-200/50 dark:border-surface-700/50">
                <span className="text-surface-400 font-medium">Department</span>
                <span className="font-bold text-surface-800 dark:text-surface-200">
                  {user.departmentName || 'General Workspace'}
                </span>
              </div>

              <div className="flex justify-between items-center py-1 border-b border-surface-200/50 dark:border-surface-700/50">
                <span className="text-surface-400 font-medium">Title</span>
                <span className="font-bold text-surface-800 dark:text-surface-200">
                  {user.title || 'Contributor'}
                </span>
              </div>

              <div className="flex justify-between items-center py-1">
                <span className="text-surface-400 font-medium">Member Since</span>
                <span className="font-bold text-surface-800 dark:text-surface-200">
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>

            {/* Workload Stats Card */}
            <div className="p-4 rounded-2xl bg-surface-50 dark:bg-surface-800/50 border border-surface-200 dark:border-surface-700">
              <h4 className="font-bold text-xs text-surface-900 dark:text-surface-100 mb-3">
                Productivity Overview
              </h4>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="p-3 bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700">
                  <span className="text-xl font-black text-brand-600 block">
                    {profileData?.stats?.totalAssigned || 0}
                  </span>
                  <span className="text-[10px] text-surface-400 font-semibold uppercase tracking-wider">
                    Assigned Tasks
                  </span>
                </div>
                <div className="p-3 bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700">
                  <span className="text-xl font-black text-emerald-500 block">
                    {profileData?.stats?.completedTasks || 0}
                  </span>
                  <span className="text-[10px] text-surface-400 font-semibold uppercase tracking-wider">
                    Tasks Completed
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Self Profile Modal */}
      <EditProfileModal
        isOpen={isEditSelfOpen}
        onClose={() => setIsEditSelfOpen(false)}
      />
    </div>
  );

  // If docked alongside chat in desktop view
  if (isDocked) {
    return (
      <>
        {/* Desktop Docked Sidebar */}
        <div className="hidden md:flex w-[420px] lg:w-[460px] xl:w-[480px] h-full shrink-0 border-l border-surface-200 dark:border-surface-800 bg-white dark:bg-surface-900 flex-col z-20 animate-slide-left shadow-lg">
          {drawerContent}
        </div>
        {/* Mobile Full Screen Overlay */}
        <div className="md:hidden fixed inset-0 z-[70] overflow-hidden flex justify-end">
          <div
            className="fixed inset-0 bg-surface-950/40 backdrop-blur-[2px] transition-opacity animate-in fade-in"
            onClick={onClose}
          />
          <div className="relative w-full h-full bg-white dark:bg-surface-900 shadow-2xl flex flex-col z-10 animate-slide-left">
            {drawerContent}
          </div>
        </div>
      </>
    );
  }

  // Floating Overlay (for mobile or modal usage)
  return (
    <div className="fixed inset-0 z-[70] overflow-hidden flex justify-end">
      <div
        className="fixed inset-0 bg-surface-950/40 backdrop-blur-[2px] transition-opacity animate-in fade-in"
        onClick={onClose}
      />
      <div className="relative w-full sm:w-[480px] md:w-[520px] max-w-full h-full bg-white dark:bg-surface-900 shadow-2xl border-l border-surface-200 dark:border-surface-800 flex flex-col z-10 animate-slide-left">
        {drawerContent}
      </div>
    </div>
  );
};
